import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LOCAL_STORAGE } from '@constants';
import { useChangePreventer, useRemovalPreventer } from 'utils/hooks/useMutationObserver';

import { useCaptcha } from './withCaptcha';
import { GeetestSolution } from './Captcha.types';
import useNetwork from './useNetwork';
import Geetest from './Geetest';

type CaptchaProps = {
  className?: string;
  onSuccess?: (solution: GeetestSolution) => void;
  warningText?: string;
};

const langMapping: { [key: string]: string } = {
  en: 'eng',
  es: 'spa',
  pt: 'pon',
};

const Captcha = function ({ className, onSuccess, warningText }: CaptchaProps): JSX.Element | null {
  const {
    captchaConfig,
    captchaErrorCallback,
    captchaIsLoading,
    captchaIsMounted,
    captchaSolution,
    captchaSuccessCallback,
  } = useCaptcha();

  const warningElementId = 'captcha-warning';
  const container = useRef<HTMLDivElement>(null);
  const warningElement = useRef<HTMLDivElement>(null);
  useRemovalPreventer(container, warningElementId);
  useChangePreventer(warningElement);

  const { isOnline } = useNetwork();

  const { t } = useTranslation(['ALERTS', 'STATIC']);

  const userLanguage = langMapping[localStorage.getItem(LOCAL_STORAGE.LANGUAGE) || 'en'];

  const [lang, setLang] = useState(userLanguage);
  const [ready, setReady] = useState(false);

  // Changing the language will throw an `old challenge` error because of the re-rendering.
  // In `handleError`, we reload the widget to show it with the new language.
  useEffect(() => {
    setLang(userLanguage);
  }, [userLanguage]);

  useEffect(() => {
    if (captchaSolution && onSuccess) {
      onSuccess(captchaSolution);
    }
  }, [captchaSolution]);

  const handleError = () => {
    if (ready) {
      setReady(false);
    } else {
      // On network error, default to English instead of Chinese
      setLang(langMapping.en);
    }
    captchaErrorCallback();
  };

  const handleSuccess = (solution: GeetestSolution) => {
    captchaSuccessCallback(solution);
  };

  if (!captchaIsMounted || !captchaConfig) {
    return null;
  }

  if (!isOnline) {
    return <span>{t('ALERTS:OFFLINE_CAPTCHA_MESSAGE')}</span>;
  }

  if (captchaIsLoading) {
    return null;
  }

  return (
    <div ref={container} className={className}>
      {warningText && (
        <div
          id={warningElementId}
          ref={warningElement}
          className="alert alert--warning text--sm text--left u-mb-2"
        >
          {warningText}
        </div>
      )}
      {!ready && <div className="text--md pb-2 text--center">{t('STATIC:CAPTCHA_LOADING')}</div>}
      <Geetest
        onReady={() => setReady(true)}
        onSuccess={handleSuccess}
        onError={handleError}
        language={lang}
        width="100%"
        product="float"
        config={captchaConfig}
      />
    </div>
  );
};

Captcha.defaultProps = {
  className: undefined,
  onSuccess: undefined,
  warningText: undefined,
};

export default Captcha;
